import moment from "moment-timezone";
import {
  SPR_DATE_TIME_FORMAT,
  SPR_HISTORY_DATE_FORMAT,
  SPR_HISTORY_TIME_FORMAT,
  SPR_HISTORY_TIMEZONE,
  SPR_SENDER_ROLE_LABEL,
  VERSION_LABEL,
} from "../utils/constants";

const formatOrNull = (iso, format) => (iso ? moment(iso).format(format) : null);

/**
 * The `spr` summary on a host screen's course row → entry-block view model.
 * Source: GET /mentors/studentManagment/dashboard `studentList[].courses[].spr` and
 * GET /getStudentProfle `studentPreferences[].spr` (BE doc 2026-10-19, section 4).
 * `draft` is null unless BE says a draft exists, so the block shows its card on a plain
 * truthiness check; `lastSent` is null when nothing was ever sent (`lastSentAt` null), and then
 * the block shows neither the "Last sent to parent" line nor View history.
 *
 * @param {Object} spr - `{ subjectKey, isDraftAvailable, autoId, autoBuiltAt, draftId,
 *   draftUpdatedAt, lastSentAt }`
 * @returns {{ subjectKey, automated: { generatedAtLabel }, draft: { id, savedAtLabel } | null,
 *   lastSent: { sentAtLabel } | null }}
 */
export const mapSprSummary = (spr) => ({
  subjectKey: spr?.subjectKey ?? null,
  automated: { generatedAtLabel: formatOrNull(spr?.autoBuiltAt, SPR_DATE_TIME_FORMAT) },
  draft: spr?.isDraftAvailable
    ? {
        id: spr?.draftId ?? null,
        savedAtLabel: formatOrNull(spr?.draftUpdatedAt, SPR_DATE_TIME_FORMAT),
      }
    : null,
  lastSent: spr?.lastSentAt
    ? { sentAtLabel: formatOrNull(spr.lastSentAt, SPR_DATE_TIME_FORMAT) }
    : null,
});

const inIst = (iso, format) => (iso ? moment(iso).tz(SPR_HISTORY_TIMEZONE).format(format) : "");
const roleLabel = (role) => SPR_SENDER_ROLE_LABEL?.[role] ?? role ?? "";

/**
 * POST /spr-history → the SPR History popup's rows, newest first as BE sends them.
 * `sentAt` (ISO UTC) is split into Date and Time in IST; the role label is ours (BE sends the raw
 * admin_login role). A deleted sender (`sentBy: null`) shows a dash.
 *
 * @param {Object} raw - `{ success, history: [{ sentId, type, sentAt, sentBy }] }`
 * @returns {{ sentId: number, dateLabel: string, timeLabel: string, sentBy: string,
 *   role: string }[]}
 */
export const mapSprHistory = (raw) =>
  (raw?.history ?? [])?.map((row) => ({
    sentId: row?.sentId,
    dateLabel: inIst(row?.sentAt, SPR_HISTORY_DATE_FORMAT),
    timeLabel: inIst(row?.sentAt, SPR_HISTORY_TIME_FORMAT),
    sentBy: row?.sentBy?.name || "—",
    role: row?.sentBy ? roleLabel(row?.sentBy?.role) : "—",
  }));

/**
 * The `sent` block of GET /spr/sent-preview/:sentId → the sent-report page's banner.
 *
 * @param {Object|null} sent - `{ sentId, type, sentAt, sentBy: { name, role } | null }`
 * @returns {{ sentAtLabel: string, versionLabel: string, sentBy: string, role: string }|null}
 */
export const mapSprSent = (sent) =>
  sent
    ? {
        sentAtLabel: inIst(sent?.sentAt, SPR_DATE_TIME_FORMAT),
        versionLabel: VERSION_LABEL?.[sent?.type] ?? "",
        sentBy: sent?.sentBy?.name ?? "",
        role: sent?.sentBy ? roleLabel(sent?.sentBy?.role) : "",
      }
    : null;
